// Контекст поездок: список, текущая и операции над ними.
//
// Гость живёт без него: у него нет сервера, а значит и нескольких поездок — вся
// его работа лежит в одном локальном состоянии. Поэтому статус 'guest' отделён
// от 'loading', и список не мигает пустотой, пока идёт первый запрос.
//
// Поездки бывают своими и общими: в списке лежат и те, куда человека позвали
// соавтором. Различает их роль в самой строке, отдельного запроса нет.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createStorage, emptyState, type StorageAdapter } from '../store';
import type { AppState } from '../types';
import { useAuth } from '../auth';
import * as api from './api';
import type { TripMeta } from './api';

/** Какая поездка открыта. Это настройка вида, а не часть состояния поездки. */
export const CURRENT_TRIP_KEY = 'plan-flights.trip.v1';

export type TripsStatus = 'loading' | 'ready' | 'guest';

export interface TripsApi {
  status: TripsStatus;
  trips: TripMeta[];
  /** Свои поездки — те, где человек владелец. */
  owned: TripMeta[];
  /** Общие: позвали соавтором, владелец кто-то другой. */
  shared: TripMeta[];
  /**
   * id открытой поездки. null — поездок на сервере ещё нет: состояние уедет
   * в ту, что сервер заведёт при первом сохранении (так работал и первый вход
   * до появления списка).
   */
  currentId: string | null;
  current: TripMeta | null;
  /**
   * Открытую поездку правит не только этот человек: у неё есть соавторы либо
   * он сам в ней гость. Синхронизация по этому признаку начинает подтягивать
   * чужие правки, не дожидаясь перезагрузки страницы.
   */
  collaborative: boolean;
  select(id: string): void;
  /**
   * Заводит поездку. `state` — начальное содержимое: так поездка рождается из
   * шаблона уже собранной, без пустого экрана между созданием и первой правкой.
   * По умолчанию пустое состояние.
   *
   * @returns созданная поездка либо null, если сервер отказал.
   */
  create(name: string, state?: AppState): Promise<TripMeta | null>;
  rename(id: string, name: string): Promise<void>;
  remove(id: string): Promise<void>;
  /** Выйти из чужой поездки: доступ снимается, сама поездка остаётся у владельца. */
  leave(id: string, userId: string): Promise<void>;
  refresh(): Promise<void>;
}

const TripsContext = createContext<TripsApi | undefined>(undefined);

/** Мусор в хранилище трактуем как «поездка не выбрана». */
function readStored(storage: StorageAdapter): string | null {
  const raw = storage.get(CURRENT_TRIP_KEY);
  return raw && raw.length > 0 ? raw : null;
}

/**
 * ?trip=<id> в адресе — так открываются ссылки из писем-приглашений: они ведут
 * прямо на поездку, а не на список. Параметр читается один раз при загрузке.
 */
function tripFromQuery(): string | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('trip');
  return value && /^[A-Za-z0-9._-]{1,64}$/.test(value) ? value : null;
}

/** Убирает ?trip= из адреса, не трогая остальные параметры и историю. */
function stripTripQuery(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('trip');
  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Сохранённый id мог остаться от другого аккаунта или от удалённой поездки —
 * тогда открывается самая свежая из имеющихся.
 */
function pickCurrent(trips: TripMeta[], stored: string | null): string | null {
  if (trips.length === 0) return null;
  if (stored && trips.some((t) => t.id === stored)) return stored;
  return trips[0].id;
}

export function TripsProvider({ children }: { children: ReactNode }): ReactElement {
  const { status: authStatus, user, requestSignIn } = useAuth();
  const [storage] = useState<StorageAdapter>(() => createStorage());

  const [trips, setTrips] = useState<TripMeta[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(() => readStored(storage));
  const [status, setStatus] = useState<TripsStatus>('loading');

  // Свежий список без перезапуска эффектов, которые его не ждут.
  const currentRef = useRef(currentId);
  currentRef.current = currentId;

  // Поездка из ссылки-приглашения. Живёт до первого списка, в котором она
  // нашлась: у гостя списка ещё нет, а после регистрации load() перезапустится
  // и параметр наконец сработает.
  const wantedRef = useRef<string | null>(tripFromQuery());

  const load = useCallback(async () => {
    const res = await api.listTrips();
    if (!res.ok) {
      // Сервер не ответил — список пуст, но приложение продолжает работать:
      // синхронизация сама уйдёт в локальный режим.
      setTrips([]);
      setStatus('ready');
      return;
    }
    const list = res.value?.trips ?? [];
    setTrips(list);
    const wanted = wantedRef.current;
    if (wanted) {
      // Поездка из ссылки открывается только когда она есть в списке
      // доступных этому пользователю; чужой или несуществующий id просто
      // гаснет — человек остаётся на своей последней поездке.
      wantedRef.current = null;
      stripTripQuery();
      if (list.some((t) => t.id === wanted)) {
        setCurrentId(wanted);
        setStatus('ready');
        return;
      }
    }
    setCurrentId(pickCurrent(list, currentRef.current));
    setStatus('ready');
  }, []);

  useEffect(() => {
    if (authStatus === 'loading') {
      setStatus('loading');
      return;
    }
    if (authStatus === 'guest') {
      setTrips([]);
      setCurrentId(null);
      setStatus('guest');
      // Ссылка на поездку в руках гостя: поездка показывается только своим,
      // поэтому сразу открывается форма входа. Параметр переживает вход и
      // регистрацию — после них load() откроет именно эту поездку.
      if (wantedRef.current) requestSignIn();
      return;
    }
    setStatus('loading');
    void load();
    // Смена пользователя перечитывает список целиком.
  }, [authStatus, user?.id, load, requestSignIn]);

  useEffect(() => {
    if (currentId) storage.set(CURRENT_TRIP_KEY, currentId);
    else storage.remove(CURRENT_TRIP_KEY);
  }, [storage, currentId]);

  const select = useCallback((id: string) => setCurrentId(id), []);

  const create = useCallback<TripsApi['create']>(async (name, state) => {
    const res = await api.createTrip(name, state ?? emptyState());
    if (!res.ok || !res.value?.trip) return null;
    const trip = res.value.trip;
    setTrips((prev) => [trip, ...prev]);
    setCurrentId(trip.id);
    return trip;
  }, []);

  const rename = useCallback<TripsApi['rename']>(async (id, name) => {
    const res = await api.renameTrip(id, name);
    if (!res.ok || !res.value?.trip) return;
    const trip = res.value.trip;
    setTrips((prev) => prev.map((t) => (t.id === trip.id ? trip : t)));
  }, []);

  /** Убрать поездку из списка, не потеряв открытую: удаление и выход делают это одинаково. */
  const forget = useCallback((id: string) => {
    setTrips((prev) => {
      const next = prev.filter((t) => t.id !== id);
      // Ушла та, что была открыта — переходим к самой свежей из оставшихся.
      setCurrentId((cur) => (cur === id ? pickCurrent(next, null) : cur));
      return next;
    });
  }, []);

  const remove = useCallback<TripsApi['remove']>(
    async (id) => {
      const res = await api.deleteTrip(id);
      if (!res.ok) return;
      forget(id);
    },
    [forget],
  );

  const leave = useCallback<TripsApi['leave']>(
    async (id, userId) => {
      const res = await api.removeMember(id, userId);
      if (!res.ok) return;
      forget(id);
    },
    [forget],
  );

  const value = useMemo<TripsApi>(() => {
    const current = trips.find((t) => t.id === currentId) ?? null;
    return {
      status,
      trips,
      owned: trips.filter((t) => t.role !== 'editor'),
      shared: trips.filter((t) => t.role === 'editor'),
      currentId,
      current,
      collaborative: current !== null && (current.role !== 'owner' || current.members > 0),
      select,
      create,
      rename,
      remove,
      leave,
      refresh: load,
    };
  }, [status, trips, currentId, select, create, rename, remove, leave, load]);

  return <TripsContext.Provider value={value}>{children}</TripsContext.Provider>;
}

export function useTrips(): TripsApi {
  const ctx = useContext(TripsContext);
  if (ctx === undefined) throw new Error('useTrips() должен использоваться внутри <TripsProvider>');
  return ctx;
}
